<template>
  <div>
    <!-- Use the existing WalletHeader component -->
    <wallet-header :walletData="walletData" />

    <!-- Withdrawal Request Form -->
    <div class="p-4 shadow-sm rounded withdraw-section">
      <div class="form-section withdraw-form">
        <h5 class="fw-bold">Withdrawal Request</h5>
        <p class="text-muted small">
          Enter your details below to withdraw money
        </p>

        <div v-if="successMessage" class="alert alert-success mb-4">
          {{ successMessage }}
        </div>

        <div v-if="errorMessage" class="alert alert-danger mb-4">
          {{ errorMessage }}
        </div>

        <form @submit.prevent="submitWithdrawal">
          <div class="mb-3">
            <label class="form-label fw-semibold">Enter amount</label>
            <input
              type="text"
              class="form-control"
              placeholder="5000 Btc"
              v-model="form.amount"
              :class="{ 'is-invalid': errors.amount }"
            />
            <div v-if="errors.amount" class="invalid-feedback">
              {{ errors.amount }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Email</label>
            <input
              type="email"
              class="form-control"
              placeholder="johndoe@gmail.com"
              v-model="form.email"
              :class="{ 'is-invalid': errors.email }"
            />
            <div v-if="errors.email" class="invalid-feedback">
              {{ errors.email }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold"
              >Binance wallet address / Id</label
            >
            <input
              type="text"
              class="form-control"
              placeholder="342957823"
              v-model="form.walletAddress"
              :class="{ 'is-invalid': errors.walletAddress }"
            />
            <div v-if="errors.walletAddress" class="invalid-feedback">
              {{ errors.walletAddress }}
            </div>
          </div>

          <div class="border-dashed my-4"></div>

          <p class="summary">Transaction Summary:</p>
          <div class="d-flex justify-content-between text-muted mb-3">
            <span>Amount</span>
            <span>{{ form.amount || "0.000000" }} Btc</span>
          </div>
          <div class="d-flex justify-content-between text-muted mb-3">
            <span>Processing Fee ({{ processingFeeRate }}%)</span>
            <span>{{ calculateFee() }} Btc</span>
          </div>
          <div class="d-flex justify-content-between text-muted mb-3">
            <span>Total</span>
            <span>{{ calculateTotal() }} Btc</span>
          </div>

          <div class="border-dashed my-4"></div>

          <div class="d-flex gap-3">
            <button
              type="submit"
              class="btn withdraw-btn px-4"
              :disabled="isSubmitting || !canWithdraw"
            >
              {{ isSubmitting ? "Processing..." : "Withdraw" }}
            </button>
            <button
              type="button"
              class="btn btn-outline-dark cancel-btn px-4"
              @click="cancelWithdrawal"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import WalletHeader from "@/components/wallet/WalletHeader.vue";
import { mapActions } from "vuex";

export default {
  name: "WithdrawalRequest",
  components: {
    WalletHeader,
  },
  head: {
    link: [
      {
        rel: "stylesheet",
        href: "/assets/css/wallet.css",
      },
    ],
  },
  data() {
    return {
      walletData: null,
      form: {
        amount: "",
        email: "",
        walletAddress: "",
      },
      errors: {},
      isSubmitting: false,
      successMessage: "",
      errorMessage: "",
      processingFeeRate: 1.5,
    };
  },
  computed: {
    canWithdraw() {
      // Check if user is verified
      if (!this.walletData?.user?.verified) {
        return false;
      }

      // Check if user has sufficient balance
      const amount = parseFloat(this.form.amount || 0);
      const balance = parseFloat(this.walletData?.walletData?.balance || 0);

      return balance > 0 && amount > 0 && amount <= balance;
    },
  },
  methods: {
    ...mapActions("user", ["getWalletData", "submitWithdrawalRequest"]),

    calculateFee() {
      if (!this.form.amount || isNaN(parseFloat(this.form.amount))) {
        return "0.000000";
      }

      const amount = parseFloat(this.form.amount);
      const fee = amount * (this.processingFeeRate / 100);
      return fee.toFixed(6);
    },

    calculateTotal() {
      if (!this.form.amount || isNaN(parseFloat(this.form.amount))) {
        return "0.000000";
      }

      const amount = parseFloat(this.form.amount);
      const fee = amount * (this.processingFeeRate / 100);
      return (amount + fee).toFixed(6);
    },

    validateForm() {
      this.errors = {};
      let isValid = true;

      // Validate amount
      if (!this.form.amount) {
        this.errors.amount = "Amount is required";
        isValid = false;
      } else if (isNaN(parseFloat(this.form.amount))) {
        this.errors.amount = "Amount must be a valid number";
        isValid = false;
      } else if (parseFloat(this.form.amount) <= 0) {
        this.errors.amount = "Amount must be greater than 0";
        isValid = false;
      } else if (
        parseFloat(this.form.amount) >
        parseFloat(this.walletData?.walletData?.balance || 0)
      ) {
        this.errors.amount = "Amount exceeds available balance";
        isValid = false;
      }

      // Validate email
      if (!this.form.email) {
        this.errors.email = "Email is required";
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) {
        this.errors.email = "Please enter a valid email address";
        isValid = false;
      }

      // Validate wallet address
      if (!this.form.walletAddress) {
        this.errors.walletAddress = "Wallet address/ID is required";
        isValid = false;
      }

      return isValid;
    },

    async submitWithdrawal() {
      if (!this.validateForm()) return;

      if (!this.walletData?.user?.verified) {
        this.errorMessage =
          "Your account must be verified before you can withdraw funds.";
        global.app.$toastr.error(this.errorMessage);
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const fee = parseFloat(this.calculateFee());
        const total = parseFloat(this.calculateTotal());

        const payload = {
          amount: parseFloat(this.form.amount),
          email: this.form.email,
          walletAddress: this.form.walletAddress,
          fee: fee,
          total: total,
          currency: this.walletData?.walletData?.currency || "BTC",
        };

        const response = await this.submitWithdrawalRequest(payload);

        if (response && response.status) {
          global.app.$toastr.success(
            "Withdrawal request submitted successfully!"
          );

          // Optionally disable submit button for UX
          this.isSubmitting = true;

          // Redirect to wallet after delay
          setTimeout(() => {
            this.$router.push("/wallet");
          }, 1500);
        } else {
          this.errorMessage =
            response?.message ||
            "Failed to submit withdrawal request. Please try again.";
          global.app.$toastr.error(this.errorMessage);
        }
      } catch (error) {
        console.error("Withdrawal error:", error);
        this.errorMessage =
          "An error occurred while processing your request. Please try again.";
        global.app.$toastr.error(this.errorMessage);
      } finally {
        // Allow button re-enabling only if not redirecting
        setTimeout(() => {
          this.isSubmitting = false;
        }, 2000);
      }
    },

    cancelWithdrawal() {
      // Navigate back to wallet page
      this.$router.push("/wallet");
    },
  },
  async created() {
    try {
      // Fetch wallet data
      const response = await this.getWalletData();
      this.walletData = response;
      console.log("Wallet data:", this.walletData);

      // Check if user is verified
      if (!this.walletData?.user?.verified) {
        this.errorMessage =
          "Your account must be verified before you can withdraw funds.";
      }
      // Pre-populate email if available
      if (this.walletData?.user?.email) {
        this.form.email = this.walletData.user.email;
      }
      if (this.walletData?.user?.walletAddress) {
        this.form.walletAddress = this.walletData.user.walletAddress;
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      this.errorMessage =
        "Failed to load wallet data. Please refresh the page.";
    }
  },
};
</script>

<style scoped>
.withdraw-section {
  background-color: #fff;
  margin: 30px auto;
  max-width: 800px;
}

.withdraw-form {
  max-width: 600px;
  margin: 0 auto;
}

.border-dashed {
  border-top: 1px dashed #ccc;
}

.summary {
  font-weight: 600;
  margin-bottom: 15px;
}

.withdraw-btn {
  background-color: #fcb913;
  color: #000;
  font-weight: 600;
}

.withdraw-btn:hover {
  background-color: #e6a700;
}

.cancel-btn {
  font-weight: 600;
}
</style>
